import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, apiPut, apiDelete, operationsClient } from '@/lib/api';
import { tagEndpoints } from '@/lib/api/endpoints';
import { tagKeys } from '@/lib/queryKeys';
import type { Tag, CreateTagDto, TagScope, UpdateTagDto } from '@/types/estimate';
import { useHasPermission } from '@/hooks/usePermissions';

export function useTags(scope: string, options: { enabled?: boolean } = {}) {
  const canViewTags = useHasPermission('tags:view');
  const query = useApiQuery<Tag[]>({
    queryKey: tagKeys.lists(scope),
    queryFn: () => apiGet<Tag[]>(operationsClient, tagEndpoints.list(scope)),
    enabled: canViewTags && (options.enabled ?? true),
    showErrorToast: false,
  });
  return canViewTags ? query : { ...query, data: undefined };
}

export function useCreateTag(scope: string) {
  const queryClient = useQueryClient();
  return useApiMutation<Tag, Omit<CreateTagDto, 'scope'>>({
    mutationFn: (dto) =>
      apiPost<Tag, CreateTagDto>(operationsClient, tagEndpoints.create(), {
        ...dto,
        scope: scope as TagScope,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.lists(scope) });
      toast.success('Tag created');
    },
  });
}

export function useUpdateTag(id: string, scope: string) {
  const queryClient = useQueryClient();
  return useApiMutation<Tag, UpdateTagDto>({
    mutationFn: (dto) => apiPut<Tag, UpdateTagDto>(operationsClient, tagEndpoints.update(id), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.lists(scope) });
      toast.success('Tag updated');
    },
  });
}

export function useDeleteTag(scope: string) {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, tagEndpoints.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.lists(scope) });
      toast.success('Tag deleted');
    },
  });
}
